import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { BaseView } from '@/components/base'
import { ShareQRCode } from '@/components/ShareQRCode'
import { ScanQRCode } from '@/components/ScanQRCode'
import { SwitchButtonAnimated } from '@/components/SwitchButtonAnimated'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'
import { parseQRCode } from '@/utils/qrCodeParser'
import useOffsets from '@/hooks/useOffsets'

export const ShareQRScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const [isScan, setIsScan] = useState(false)

  const handleScanResult = (result: string) => {
    const qrResult = parseQRCode(result)

    switch (qrResult.type) {
      case 'user-profile':
        if (qrResult.data.userId) {
          navigate(`/user-profile/${qrResult.data.userId}`, {
            state: { byScan: true },
          })
        } else {
          toast.error(t('translation:invalidQRCode', 'Invalid QR code'))
        }
        break

      case 'payment-request':
        if (qrResult.data.paymentData) {
          navigate('/send-request', {
            state: {
              type: qrResult.data.paymentData.type,
              recipient: {
                id: qrResult.data.paymentData.id,
                name: qrResult.data.paymentData.name,
                username: qrResult.data.paymentData.username,
                avatar: qrResult.data.paymentData.avatar,
              },
              // Pre-fill amount if available
              ...(qrResult.data.paymentData.currencyValue && {
                currencyValue: qrResult.data.paymentData.currencyValue,
                fiatValue: qrResult.data.paymentData.fiatValue,
                fiatCode: qrResult.data.paymentData.fiatCode,
              }),
            },
          })
        } else {
          toast.error(
            t('translation:invalidPaymentRequest', 'Invalid payment request'),
          )
        }
        break

      case 'wallet-address':
        if (qrResult.data.walletAddress) {
          // Navigate to withdraw with pre-filled address
          navigate('/withdraw', {
            state: {
              walletAddress: qrResult.data.walletAddress,
              fromQRScan: true,
            },
          })
        } else {
          toast.error(
            t('translation:invalidWalletAddress', 'Invalid wallet address'),
          )
        }
        break

      case 'unknown':
      default:
        toast.error(
          t('translation:unsupportedQRCode', 'Unsupported QR code format'),
        )
        break
    }

    // Switch back to QR display mode after successful scan
    setIsScan(false)
  }

  const handleRejectCameraPermission = () => {
    setIsScan(false)
  }

  return (
    <>
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        // title={t('shareQRCode', 'Share QR Code')}
        showBackButton={true}
        showBackground={false}
      />

      {/* Content */}
      <BaseView
        flex={1}
        px={12}
        center
        justify="center"
        bgColor="background.screen"
        style={{
          paddingBottom: '160px', // Space for fixed switch button
        }}
        data-component="share-qr-screen"
      >
        {/* Main content area */}
        <BaseView center justify="center" gap={32}>
          {isScan ? (
            <ScanQRCode
              onScanResult={handleScanResult}
              onRejectPermission={handleRejectCameraPermission}
            />
          ) : (
            <ShareQRCode />
          )}
        </BaseView>
      </BaseView>

      {/* Fixed Switch Button */}
      <BaseView
        center
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          paddingTop: isScan ? '50px' : '30px',
          paddingBottom: `${offsets.bottom}px`,
          zIndex: 100,
        }}
      >
        <SwitchButtonAnimated
          leftLabel={t('translation:scan', 'Scan')}
          rightLabel={t('translation:myProfile', 'My profile')}
          isLeftButtonActive={isScan}
          onLeftTabClicked={() => setIsScan(true)}
          onRightTabClicked={() => setIsScan(false)}
        />
      </BaseView>
    </>
  )
}
